import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/ui/spinner";
import { ReactSelect } from "@/components/ui/react-select";
import {
  PAPER_SIZE_PRESETS,
  PAPER_SIZE_OPTIONS,
  ORIENTATIONS,
  UNITS,
  STANDARD_DIMENSIONS,
} from "./printerConstants";
import { Ruler, Sparkles, Check, Info } from "lucide-react";
import { cn } from "@/lib/utils";

export default function PaperSizeFormDialog({
  open,
  onClose,
  onSubmit,
  editMode,
  form,
  setForm,
  printers = [],
  hidePrinterSelect = false,
  isPending,
}) {
  const isCustom = form.size === "custom";

  const handleApplyPreset = (preset) => {
    setForm((prev) => ({
      ...prev,
      presetId: preset.id,
      label: prev.label.trim() === "" || PAPER_SIZE_PRESETS.some((p) => p.label === prev.label)
        ? preset.label
        : prev.label,
      size: preset.size,
      width: preset.width ?? "",
      height: preset.height ?? "",
      unit: preset.unit ?? "mm",
      orientation: preset.orientation ?? "portrait",
    }));
  };

  const handleSizeChange = (newSize) => {
    const std = STANDARD_DIMENSIONS[newSize];
    setForm((prev) => ({
      ...prev,
      size: newSize,
      width: std ? std.width : prev.width,
      height: std ? std.height : prev.height,
      unit: std ? std.unit : prev.unit,
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[92vh] flex flex-col p-0 gap-0 overflow-hidden bg-background">
        <DialogHeader className="px-6 py-4 border-b border-border bg-card shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0">
              <Ruler className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold">
                {editMode ? "Edit Paper Size" : "Configure Paper Size"}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                {editMode
                  ? "Adjust paper dimensions, orientation, or label"
                  : "Choose a 1-click paper preset or specify custom dimensions"}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Quick Presets (Shown in both Add and Edit for quick convenience) */}
          <div className="space-y-2.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                Quick Paper Presets
              </Label>
              <span className="text-[11px] text-muted-foreground">Click to auto-fill</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {PAPER_SIZE_PRESETS.map((p) => {
                const isSelected =
                  form.presetId === p.id ||
                  (form.size === p.size &&
                    Number(form.width) === Number(p.width) &&
                    Number(form.height) === Number(p.height));

                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => handleApplyPreset(p)}
                    className={cn(
                      "flex flex-col items-start p-2.5 rounded-lg border text-left transition-all duration-150 cursor-pointer relative",
                      isSelected
                        ? "border-primary bg-primary/5 shadow-xs ring-1 ring-primary/30"
                        : "border-border/80 hover:border-border hover:bg-muted/40"
                    )}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="h-2 w-2" />
                      </div>
                    )}
                    <span className="text-xs font-semibold text-foreground line-clamp-1">
                      {p.shortLabel}
                    </span>
                    <span className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">
                      {p.badge}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Printer Selection (if not hidden) */}
          {!hidePrinterSelect && (
            <div className="space-y-1.5">
              <Label className="text-xs">
                Printer <span className="text-destructive">*</span>
              </Label>
              <ReactSelect
                options={printers.map((p) => ({
                  value: p.id,
                  label: p.name,
                }))}
                value={
                  printers
                    .map((p) => ({ value: p.id, label: p.name }))
                    .find((p) => p.value === form.printer_id) || null
                }
                onChange={(opt) =>
                  setForm((prev) => ({ ...prev, printer_id: opt?.value || "" }))
                }
                placeholder="Select printer…"
              />
            </div>
          )}

          {/* Label & Standard Whitelist Size */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="paper-label" className="text-xs">
                Paper Label <span className="text-destructive">*</span>
              </Label>
              <Input
                id="paper-label"
                placeholder="e.g. 80mm Thermal Receipt"
                value={form.label}
                onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">
                Base Size Whitelist <span className="text-destructive">*</span>
              </Label>
              <ReactSelect
                options={PAPER_SIZE_OPTIONS}
                value={
                  PAPER_SIZE_OPTIONS.find((s) => s.value === form.size) || {
                    value: form.size,
                    label: form.size,
                  }
                }
                onChange={(opt) => handleSizeChange(opt?.value || "custom")}
              />
            </div>
          </div>

          {/* Dimensions Section */}
          <div className="p-3.5 rounded-xl border border-border bg-muted/10 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-foreground">
                Dimensions & Orientation
              </span>
              {isCustom && Number(form.height) === 0 && (
                <span className="text-[11px] text-primary font-medium flex items-center gap-1">
                  <Info className="h-3 w-3" /> Continuous Roll (Thermal POS)
                </span>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="paper-width" className="text-xs">
                  Width {isCustom && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id="paper-width"
                  type="number"
                  step="any"
                  placeholder="3.15"
                  value={form.width}
                  onChange={(e) => setForm((prev) => ({ ...prev, width: e.target.value }))}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="paper-height" className="text-xs">
                  Height {isCustom && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id="paper-height"
                  type="number"
                  step="any"
                  placeholder="0 for roll"
                  value={form.height}
                  onChange={(e) => setForm((prev) => ({ ...prev, height: e.target.value }))}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Unit</Label>
                <ReactSelect
                  options={UNITS}
                  value={UNITS.find((u) => u.value === form.unit) || UNITS[0]}
                  onChange={(opt) =>
                    setForm((prev) => ({ ...prev, unit: opt?.value || "mm" }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <div className="space-y-1.5">
                <Label className="text-xs">
                  Orientation <span className="text-destructive">*</span>
                </Label>
                <ReactSelect
                  options={ORIENTATIONS}
                  value={
                    ORIENTATIONS.find((o) => o.value === form.orientation) ||
                    ORIENTATIONS[0]
                  }
                  onChange={(opt) =>
                    setForm((prev) => ({
                      ...prev,
                      orientation: opt?.value || "portrait",
                    }))
                  }
                />
              </div>

              <div className="flex items-center justify-between p-2.5 rounded-lg border border-border bg-background self-end">
                <div>
                  <Label htmlFor="paper-default" className="text-xs font-medium cursor-pointer">
                    Default Paper for Printer
                  </Label>
                  <p className="text-[10px] text-muted-foreground">
                    First choice when printing to this printer
                  </p>
                </div>
                <Switch
                  id="paper-default"
                  checked={Boolean(form.is_default)}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({ ...prev, is_default: checked }))
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-3 border-t border-border bg-card flex flex-row items-center justify-between sm:justify-between shrink-0">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onSubmit}
            disabled={
              isPending ||
              !form.label.trim() ||
              (!hidePrinterSelect && !form.printer_id) ||
              (isCustom && (form.width === "" || form.height === ""))
            }
          >
            {isPending ? (
              <>
                <Spinner className="mr-2 h-3.5 w-3.5" />
                Saving…
              </>
            ) : editMode ? (
              "Save Changes"
            ) : (
              "Add Paper Size"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
